import { useState, useEffect, useCallback, type JSX } from 'react'
import {
  Mail,
  MessageSquare,
  Smartphone,
  Globe,
  Phone,
  Inbox,
  RefreshCw,
  Clock,
  UserCheck,
  Zap,
  AlertCircle,
  Search,
  Layers,
  Users,
  Megaphone,
} from 'lucide-react'
import { api } from '../../../api/client.js'
import { getSocket } from '../../../socket/client.js'
import Select from '../../../components/ui/Select.js'
import ConversationDetailPanel from '../../../components/conversations/detail/ConversationDetailPanel.js'
import { formatCustomerName } from '../../../utils/formatCustomerName.js'
import OmnichannelWorkloadPanel, {
  initials,
  type OmniQueueSettings,
  type WorkloadResponse,
} from '../../../components/agent/OmnichannelWorkloadPanel.js'
import OmnichannelStrategyPicker from '../../../components/agent/OmnichannelStrategyPicker.js'
import { useToast } from '../../../components/system/ToastProvider.js'
import { useAuthStore } from '../../../store/authStore.js'
import { formatInTimezone } from '../../../utils/timezone.js'

interface OmnichannelQueueItem {
  id: string
  conversationId: string | null
  referenceId: string | null
  channelOrigin: string
  customerHandle: string | null
  customerName: string | null
  subject: string | null
  priority: number
  queueId: string | null
  teamId: string | null
  campaignId: string | null
  campaignName: string | null
  assignedAgentId: string | null
  assignedAgentName: string | null
  assignedAgentExtension: string | null
  assignedAt: string | null
  status: boolean
  metadata: Record<string, unknown>
  createdAt: string
  modifiedAt: string
}

/** Permissions allowed to change routing / force auto-assign (mirrors QUEUE_MANAGE in the API). */
const MANAGE_PERMS = ['*', 'supervisor:view', 'admin:settings:manage', 'conversations:reassign']

/** The server re-assigns in the background; WebSocket events keep view fresh in real time with 60s fallback. */
const POLL_MS = 60_000

const CHANNEL_CONFIG: Record<string, { icon: typeof Mail; label: string; color: string; bg: string }> = {
  email: { icon: Mail, label: 'Email', color: 'text-blue-600', bg: 'bg-blue-50' },
  sms: { icon: MessageSquare, label: 'SMS', color: 'text-emerald-600', bg: 'bg-emerald-50' },
  whatsapp: { icon: Smartphone, label: 'WhatsApp', color: 'text-green-600', bg: 'bg-green-50' },
  web: { icon: Globe, label: 'Web', color: 'text-indigo-600', bg: 'bg-indigo-50' },
  voice: { icon: Phone, label: 'Voice', color: 'text-purple-600', bg: 'bg-purple-50' },
  facebook: { icon: Globe, label: 'Facebook', color: 'text-blue-700', bg: 'bg-blue-50' },
  twitter: { icon: Globe, label: 'Twitter', color: 'text-sky-600', bg: 'bg-sky-50' },
  youtube: { icon: Globe, label: 'YouTube', color: 'text-red-600', bg: 'bg-red-50' },
}

const PRIORITY_CONFIG: Record<number, { label: string; color: string; bg: string; border: string }> = {
  0: { label: 'Normal', color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200' },
  1: { label: 'Low', color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' },
  2: { label: 'Medium', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200' },
  3: { label: 'High', color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200' },
  4: { label: 'Urgent', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const diffMs = now.getTime() - d.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  const diffHr = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHr / 24)

  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin}m ago`
  if (diffHr < 24) return `${diffHr}h ago`
  if (diffDay < 7) return `${diffDay}d ago`
  return formatInTimezone(d, { month: 'short', day: 'numeric' })
}

function formatFullDate(iso: string): string {
  return formatInTimezone(iso, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function OmnichannelQueuePage(): JSX.Element {
  const [items, setItems] = useState<OmnichannelQueueItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [channelFilter, setChannelFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState<'pending' | 'assigned' | 'all'>('pending')
  const [searchQuery, setSearchQuery] = useState('')
  const [detailId, setDetailId] = useState<string | null>(null)
  const [assigningId, setAssigningId] = useState<string | null>(null)
  const [assigningAll, setAssigningAll] = useState(false)
  const [savingSettings, setSavingSettings] = useState(false)
  const [workload, setWorkload] = useState<WorkloadResponse | null>(null)
  const [workloadLoading, setWorkloadLoading] = useState(true)
  const [workloadError, setWorkloadError] = useState<string | null>(null)
  const toast = useToast()
  const permissions = useAuthStore((s) => s.user?.permissions ?? [])
  const canManage = MANAGE_PERMS.some((p) => permissions.includes(p))

  const fetchWorkload = useCallback(async () => {
    setWorkloadLoading(true)
    try {
      setWorkload(await api.get<WorkloadResponse>('/omnichannel-queue/workload'))
      setWorkloadError(null)
    } catch (err) {
      setWorkloadError(err instanceof Error ? err.message : 'Failed to load agent workload.')
    } finally {
      setWorkloadLoading(false)
    }
  }, [])

  const fetchItems = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (channelFilter) params.set('channel', channelFilter)
      if (statusFilter === 'pending') params.set('status', 'true')
      else if (statusFilter === 'assigned') params.set('assigned', 'true')
      const qs = params.toString()
      const res = await api.get<{ items: OmnichannelQueueItem[] }>(
        `/omnichannel-queue${qs ? `?${qs}` : ''}`,
      )
      setItems(res.items ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load omnichannel queue.')
    } finally {
      if (!quiet) setLoading(false)
    }
  }, [channelFilter, statusFilter])

  const refreshAll = useCallback((quiet = false) => {
    void fetchItems(quiet)
    void fetchWorkload()
  }, [fetchItems, fetchWorkload])

  useEffect(() => {
    void fetchItems()
  }, [fetchItems])

  useEffect(() => {
    void fetchWorkload()
  }, [fetchWorkload])

  // Real-time: refresh view when queues or conversations are updated
  useEffect(() => {
    const socket = getSocket()
    let bumpTimer: ReturnType<typeof setTimeout> | null = null
    const onUpdate = (): void => {
      if (bumpTimer) clearTimeout(bumpTimer)
      bumpTimer = setTimeout(() => {
        if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
          refreshAll(true)
        }
      }, 400)
    }

    socket.on('conversation:opened', onUpdate)
    socket.on('conversation:transferred', onUpdate)
    socket.on('conversation:closed', onUpdate)
    socket.on('conversation:assigned', onUpdate)
    socket.on('chat:list:bump', onUpdate)

    const t = window.setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        refreshAll(true)
      }
    }, POLL_MS)

    return () => {
      if (bumpTimer) clearTimeout(bumpTimer)
      socket.off('conversation:opened', onUpdate)
      socket.off('conversation:transferred', onUpdate)
      socket.off('conversation:closed', onUpdate)
      socket.off('conversation:assigned', onUpdate)
      socket.off('chat:list:bump', onUpdate)
      window.clearInterval(t)
    }
  }, [refreshAll])

  async function handleAssign(item: OmnichannelQueueItem, auto = false): Promise<void> {
    setAssigningId(item.id)
    try {
      const res = await api.post<{ assignedAgentName?: string | null }>(
        `/omnichannel-queue/${item.id}/assign`,
        { auto },
      )
      toast.success(
        auto
          ? `Auto-assigned to ${res.assignedAgentName ?? 'agent'} (${workload?.strategy.label ?? 'queue strategy'})`
          : 'Conversation assigned to you',
      )
      refreshAll()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to assign.')
    } finally {
      setAssigningId(null)
    }
  }

  async function handleAssignAll(): Promise<void> {
    setAssigningAll(true)
    try {
      const res = await api.post<{ assigned: unknown[]; skipped: number }>('/omnichannel-queue/auto-assign', {})
      if (res.assigned.length > 0) {
        toast.success(
          `Assigned ${res.assigned.length} interaction${res.assigned.length === 1 ? '' : 's'}` +
            (res.skipped > 0 ? ` · ${res.skipped} still waiting for an eligible agent` : ''),
        )
      } else {
        toast.error(
          res.skipped > 0
            ? `No logged-in campaign agent available for ${res.skipped} waiting interaction${res.skipped === 1 ? '' : 's'}`
            : 'Nothing waiting',
        )
      }
      refreshAll()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to auto-assign.')
    } finally {
      setAssigningAll(false)
    }
  }

  async function handleSaveSettings(
    next: Pick<OmniQueueSettings, 'strategy' | 'agentScope' | 'maxOpenPerAgent'>,
  ): Promise<void> {
    setSavingSettings(true)
    try {
      const res = await api.put<{ assigned: number }>('/omnichannel-queue/settings', next)
      const label = workload?.strategies.find((s) => s.key === next.strategy)?.label ?? next.strategy
      toast.success(
        `Queue routing set to ${label}` + (res.assigned > 0 ? ` · ${res.assigned} waiting assigned` : ''),
      )
      refreshAll()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save routing.')
    } finally {
      setSavingSettings(false)
    }
  }

  const pending = items.filter((i) => i.status && !i.assignedAgentId)
  const assigned = items.filter((i) => i.assignedAgentId)

  const filteredItems = items.filter((item) => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return (
      item.subject?.toLowerCase().includes(q) ||
      item.customerName?.toLowerCase().includes(q) ||
      item.customerHandle?.toLowerCase().includes(q) ||
      item.channelOrigin.toLowerCase().includes(q) ||
      item.referenceId?.toLowerCase().includes(q) ||
      item.campaignName?.toLowerCase().includes(q)
    )
  })

  function getChannelConfig(channel: string) {
    return CHANNEL_CONFIG[channel] ?? { icon: Inbox, label: channel, color: 'text-slate-600', bg: 'bg-slate-50' }
  }

  function getPriorityConfig(priority: number) {
    return PRIORITY_CONFIG[priority] ?? PRIORITY_CONFIG[0]!
  }

  /**
   * Who auto-assign would hand this pending conversation to right now —
   * the server-ranked next-up for the item's campaign (team when no campaign).
   */
  function projectedAssignee(item: OmnichannelQueueItem): { name: string | null; reason: string } {
    if (workload?.settings.strategy === 'manual') return { name: null, reason: 'Manual queue — claim it' }
    const key = item.campaignId ? `c:${item.campaignId}` : item.teamId ? `t:${item.teamId}` : null
    if (!key) return { name: null, reason: 'No campaign on this conversation — claim manually' }
    const scope = workload?.campaigns.find((c) => c.key === key)
    const label = item.campaignName ?? scope?.name ?? 'campaign'
    if (!scope || scope.memberAgentIds.length === 0) return { name: null, reason: `No agents in ${label}` }
    if (!scope.nextUpAgentName) return { name: null, reason: `No logged-in agent in ${label}` }
    return { name: scope.nextUpAgentName, reason: `${workload?.strategy.label ?? 'Next'} in ${label}` }
  }

  return (
    <>
      <div className="min-h-full bg-slate-50/50">
        {/* Header Section */}
        <div className="border-b border-slate-200 bg-white px-6 py-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-[3px] bg-indigo-600 shadow-sm">
                <Layers className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Omnichannel Queue</h1>
                <p className="mt-0.5 text-sm text-slate-500">
                  Pending conversations across all channels waiting for agent assignment
                </p>
              </div>
            </div>

            {/* Stats badges */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 rounded-[3px] border border-slate-200 bg-white px-3 py-2 shadow-sm">
                <div className="flex h-2 w-2 rounded-full bg-orange-500" />
                <span className="text-sm font-semibold text-slate-700">{pending.length}</span>
                <span className="text-xs text-slate-500">Pending</span>
              </div>
              <div className="flex items-center gap-2 rounded-[3px] border border-slate-200 bg-white px-3 py-2 shadow-sm">
                <div className="flex h-2 w-2 rounded-full bg-green-500" />
                <span className="text-sm font-semibold text-slate-700">{assigned.length}</span>
                <span className="text-xs text-slate-500">Assigned</span>
              </div>
              {workload && (
                <div className="flex items-center gap-2 rounded-[3px] border border-slate-200 bg-white px-3 py-2 shadow-sm">
                  <Users className="h-3.5 w-3.5 text-indigo-500" />
                  <span className="text-sm font-semibold text-slate-700">
                    {workload.summary.routableAgents}/{workload.summary.totalAgents}
                  </span>
                  <span className="text-xs text-slate-500">Agents routable</span>
                </div>
              )}
            </div>
          </div>

          {/* Filters Bar */}
          <div className="mt-5 flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search by subject, client, channel, or reference ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-[3px] border border-slate-300 bg-white py-2 pl-10 pr-4 text-sm text-slate-700 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-200"
              />
            </div>

            {/* Channel Dropdown */}
            <Select
              ariaLabel="Filter by channel"
              value={channelFilter}
              onChange={setChannelFilter}
            >
              <option value="">All Channels</option>
              {Object.entries(CHANNEL_CONFIG).map(([ch, cfg]) => (
                <option key={ch} value={ch}>{cfg.label}</option>
              ))}
            </Select>

            {/* Status Dropdown */}
            <Select
              ariaLabel="Filter by status"
              value={statusFilter}
              onChange={(v) => setStatusFilter(v as 'pending' | 'assigned' | 'all')}
            >
              <option value="pending">Pending ({pending.length})</option>
              <option value="assigned">Assigned ({assigned.length})</option>
              <option value="all">All ({items.length})</option>
            </Select>

            {/* Refresh */}
            <button
              type="button"
              onClick={() => refreshAll()}
              className="flex items-center gap-2 rounded-[3px] border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-1 focus:ring-indigo-200"
            >
              <RefreshCw className={`h-4 w-4 ${loading || workloadLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>

            {/* Assign all pending with the current strategy */}
            {canManage && (
              <button
                type="button"
                disabled={assigningAll || pending.length === 0}
                onClick={() => void handleAssignAll()}
                className="flex items-center gap-2 rounded-[3px] bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus:ring-1 focus:ring-indigo-200 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Zap className={`h-4 w-4 ${assigningAll ? 'animate-pulse' : ''}`} />
                {assigningAll ? 'Assigning…' : 'Assign all pending'}
              </button>
            )}
          </div>

          {/* Queue strategy */}
          {workload && (
            <div className="mt-4">
              <OmnichannelStrategyPicker
                settings={workload.settings}
                strategies={workload.strategies}
                canManage={canManage}
                saving={savingSettings}
                onSave={(next) => void handleSaveSettings(next)}
              />
            </div>
          )}
        </div>

        {/* Content Area — queue on the left, agent/load-balancing panel on the right */}
        <div className="grid grid-cols-1 gap-6 px-6 py-6 xl:grid-cols-[minmax(0,1fr)_380px]">
          <div className="min-w-0">
          {/* Error Banner */}
          {error && (
            <div className="mb-6 flex items-center gap-3 rounded-[3px] border border-red-200 bg-red-50 px-4 py-3">
              <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-500" />
              <span className="flex-1 text-sm text-red-700">{error}</span>
              <button
                type="button"
                className="text-sm font-medium text-red-700 underline hover:text-red-800"
                onClick={() => void fetchItems()}
              >
                Retry
              </button>
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="animate-pulse rounded-[3px] border border-slate-200 bg-white p-5"
                >
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-[3px] bg-slate-100" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-1/3 rounded-[3px] bg-slate-100" />
                      <div className="h-3 w-1/2 rounded-[3px] bg-slate-100" />
                    </div>
                    <div className="h-8 w-24 rounded-[3px] bg-slate-100" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredItems.length === 0 ? (
            /* Empty State */
            <div className="flex flex-col items-center justify-center rounded-[3px] border border-dashed border-slate-300 bg-white py-20">
              <div className="flex h-16 w-16 items-center justify-center rounded-[3px] bg-slate-100">
                <Inbox className="h-8 w-8 text-slate-400" />
              </div>
              <h3 className="mt-4 text-base font-semibold text-slate-900">
                {searchQuery ? 'No matching conversations' : 'No conversations in queue'}
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                {searchQuery
                  ? 'Try adjusting your search or filters'
                  : 'Pending conversations will appear here when they arrive'}
              </p>
            </div>
          ) : (
            /* Card List */
            <div className="space-y-3">
              {filteredItems.map((item) => {
                const chConfig = getChannelConfig(item.channelOrigin)
                const prConfig = getPriorityConfig(item.priority)
                const ChannelIcon = chConfig.icon
                const isAssigned = !!item.assignedAgentId
                const clientName = formatCustomerName(item.customerName, item.customerHandle, item.channelOrigin)
                const projection = isAssigned ? null : projectedAssignee(item)

                return (
                  <div
                    key={item.id}
                    className={`group relative overflow-hidden rounded-[3px] border bg-white p-4 shadow-sm transition ${assigningId === item.id ? 'border-indigo-400 shadow-md' : 'border-slate-200 hover:border-indigo-300 hover:shadow-md'}`}
                  >
                    {/* Auto-assign progress bar at top */}
                    {assigningId === item.id && (
                      <div className="absolute left-0 right-0 top-0 z-20 h-0.5 bg-indigo-100">
                        <div className="h-full animate-pulse bg-indigo-600" style={{ width: '100%' }} />
                      </div>
                    )}
                    {assigningId === item.id && (
                      <div className="absolute right-3 top-3 z-20 flex items-center gap-1.5 rounded-[3px] bg-indigo-50 px-2 py-1">
                        <div className="h-3 w-3 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
                        <span className="text-xs font-medium text-indigo-700">Finding campaign agent…</span>
                      </div>
                    )}
                    <div className="flex items-start gap-4">
                      {/* Channel Icon */}
                      <div
                        className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-[3px] ${chConfig.bg}`}
                      >
                        <ChannelIcon className={`h-6 w-6 ${chConfig.color}`} />
                      </div>

                      {/* Main Content */}
                      <div
                        className="flex-1 cursor-pointer"
                        onClick={() => item.conversationId && setDetailId(item.conversationId)}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-semibold uppercase tracking-wide ${chConfig.color}`}>
                            {chConfig.label}
                          </span>
                          {item.referenceId && (
                            <>
                              <span className="text-slate-300">•</span>
                              <span className="text-xs font-medium text-slate-500">
                                {item.referenceId}
                              </span>
                            </>
                          )}
                          <span className="text-slate-300">•</span>
                          <span className="text-xs text-slate-400">
                            {formatDate(item.createdAt)}
                          </span>
                        </div>

                        <h3 className="mt-1 text-sm font-semibold text-slate-900">
                          {item.subject || 'No subject'}
                        </h3>

                        <div className="mt-2 flex flex-wrap items-center gap-3">
                          {/* Client */}
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-slate-400">Client:</span>
                            <span className="text-xs font-medium text-slate-700">
                              {clientName || 'Unknown'}
                            </span>
                          </div>

                          {/* Campaign — decides who may receive it */}
                          <span
                            className={`inline-flex items-center gap-1 rounded-[3px] border px-2 py-0.5 text-xs font-medium ${item.campaignName ? 'border-indigo-100 bg-indigo-50 text-indigo-700' : 'border-slate-200 bg-slate-50 text-slate-400'}`}
                          >
                            <Megaphone className="h-3 w-3" />
                            {item.campaignName ?? 'No campaign'}
                          </span>

                          {/* Priority Badge */}
                          <span
                            className={`inline-flex items-center rounded-[3px] border px-2 py-0.5 text-xs font-medium ${prConfig.border} ${prConfig.bg} ${prConfig.color}`}
                          >
                            {prConfig.label}
                          </span>

                          {/* Assignment — who holds it, or who auto-assign would pick */}
                          {isAssigned ? (
                            <span className="inline-flex items-center gap-1.5 rounded-[3px] bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                              <span className="flex h-4 w-4 items-center justify-center rounded-[3px] bg-green-600 text-[8px] font-bold text-white">
                                {initials(item.assignedAgentName ?? '?')}
                              </span>
                              {item.assignedAgentName ?? 'Assigned'}
                              {item.assignedAgentExtension && (
                                <span className="font-normal text-green-600/70">
                                  ext {item.assignedAgentExtension}
                                </span>
                              )}
                            </span>
                          ) : (
                            <>
                              <span className="inline-flex items-center gap-1 rounded-[3px] bg-orange-50 px-2 py-0.5 text-xs font-medium text-orange-700">
                                <Clock className="h-3 w-3" />
                                Pending
                              </span>
                              {projection?.name ? (
                                <span
                                  className="inline-flex items-center gap-1 rounded-[3px] border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700"
                                  title={projection.reason}
                                >
                                  <Zap className="h-3 w-3" />
                                  Next → {projection.name}
                                </span>
                              ) : (
                                projection && (
                                  <span
                                    className="inline-flex items-center gap-1 rounded-[3px] border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-500"
                                    title={projection.reason}
                                  >
                                    <AlertCircle className="h-3 w-3" />
                                    {projection.reason}
                                  </span>
                                )
                              )}
                            </>
                          )}

                          {/* Full timestamp on hover */}
                          <span className="text-xs text-slate-300 opacity-0 transition group-hover:opacity-100" title={formatFullDate(item.createdAt)}>
                            {formatFullDate(item.createdAt)}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-shrink-0 items-center gap-2">
                        {!isAssigned && (
                          <>
                            <button
                              type="button"
                              disabled={assigningId === item.id}
                              className="flex items-center gap-1.5 rounded-[3px] bg-indigo-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus:ring-1 focus:ring-indigo-200 disabled:cursor-not-allowed disabled:opacity-60"
                              onClick={(e) => {
                                e.stopPropagation()
                                void handleAssign(item, true)
                              }}
                            >
                              {assigningId === item.id ? (
                                <>
                                  <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                                  Assigning…
                                </>
                              ) : (
                                <>
                                  <Zap className="h-3.5 w-3.5" />
                                  Auto Assign
                                </>
                              )}
                            </button>
                            <button
                              type="button"
                              disabled={assigningId === item.id}
                              className="flex items-center gap-1.5 rounded-[3px] border border-indigo-600 bg-white px-3 py-2 text-xs font-semibold text-indigo-600 shadow-sm transition hover:bg-indigo-50 focus:outline-none focus:ring-1 focus:ring-indigo-200 disabled:cursor-not-allowed disabled:opacity-60"
                              onClick={(e) => {
                                e.stopPropagation()
                                void handleAssign(item, false)
                              }}
                            >
                              <UserCheck className="h-3.5 w-3.5" />
                              Claim
                            </button>
                          </>
                        )}
                        {isAssigned && (
                          <span className="text-xs font-medium text-slate-400">
                            {item.assignedAt ? `Assigned ${formatDate(item.assignedAt)}` : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          </div>

          {/* Agents & load balancing */}
          <aside className="min-w-0 xl:sticky xl:top-6 xl:self-start">
            <OmnichannelWorkloadPanel
              data={workload}
              loading={workloadLoading}
              error={workloadError}
            />
          </aside>
        </div>
      </div>

      <ConversationDetailPanel
        conversationId={detailId}
        open={detailId !== null}
        onClose={() => setDetailId(null)}
        onChanged={() => void fetchItems()}
      />
    </>
  )
}
